// src/pages/LandingPage1.jsx
import React, { useMemo, useState, useEffect } from"react";
import { createPortal } from"react-dom";
import {
 CodeBracketIcon,
 RocketLaunchIcon,
 BoltIcon,
 AcademicCapIcon,
} from"@heroicons/react/24/outline";
import API_BASE_URL from"../Config/config";

/* ---------------- Shared helpers ---------------- */
const panel =
"bg-brand-midnight rounded-2xl border border-secondary p-6 sm:p-8 shadow-md";
const card =
"bg-[#151B2E] rounded-xl border border-gray-700 hover:border-primary transition-all p-5";

/* ---------------- Countdown hook ---------------- */
function useCountdown(deadline) {
 const [now, setNow] = React.useState(() => new Date());
 React.useEffect(() => {
 const t = setInterval(() => setNow(new Date()), 1000);
 return () => clearInterval(t);
 }, []);
 const diff = Math.max(0, Number(deadline) - Number(now));
 const d = Math.floor(diff / (1000 * 60 * 60 * 24));
 const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
 const m = Math.floor((diff / (1000 * 60)) % 60);
 const s = Math.floor((diff / 1000) % 60);
 return { d, h, m, s, over: diff <= 0 };
}

/* ---------------- Floating carousel (no overlap) ---------------- */
function FloatingFeatureStrip() {
 const items = [
 { title:"Language", color:"#2F7DC5", icon:"globe" },
 { title:"Business", color:"#2FB46E", icon:"bars" },
 { title:"Literature", color:"#E65050", icon:"book" },
 { title:"Design", color:"#F2994A", icon:"image" },
 { title:"Science", color:"#6C5CE7", icon:"beaker" },
 { title:"Coding", color:"#00B8D9", icon:"code" },
 ];

 return (
 <div className="absolute left-1/2 bottom-[-80px] -translate-x-1/2 w-[94%] max-w-5xl">
 <div
 className="rounded-2xl px-6 py-5 shadow-hero overflow-visible"
 style={{ backgroundColor:"#4F46E5", border:"1px solid #1E3A8A" }}
 >
 <div className="grid grid-cols-1 lg:grid-cols-[1fr_3fr] items-center gap-6">
 {/* left text */}
 <div className="text-left">
 <div className="text-white/90 font-semibold text-lg">
 Best platform to learn
 </div>
 <div className="text-white text-xl">everything</div>
 </div>

 <div className="relative px-10">
 <div className="grid grid-cols-2 xl:grid-cols-3 1xl:grid-cols-6 gap-6">
 {[
 { title:"Bootcamp", color:"#1E3A8A", icon:"globe" },
 { title:"Compiler", color:"#10B981", icon:"bars" },
 { title:"Launchpad", color:"#6366F1", icon:"code" },
 ].map((b) => (
 <div
 key={b.title}
 className="rounded-xl p-4 text-center text-white shadow"
 style={{ backgroundColor: b.color }}
 >
 <div className="mx-auto mb-2 h-10 w-10 flex items-center justify-center">
 {b.icon ==="globe" && (
 <svg
 viewBox="0 0 24 24"
 className="h-8 w-8"
 fill="none"
 stroke="white"
 strokeWidth="2"
 >
 <circle cx="12" cy="12" r="9" />
 <path d="M3 12h18M12 3c3 3.5 3 14.5 0 18M12 3c-3 3.5-3 14.5 0 18" />
 </svg>
 )}
 {b.icon ==="bars" && (
 <svg viewBox="0 0 24 24" className="h-8 w-8" fill="white">
 <rect x="4" y="12" width="3" height="8" rx="1" />
 <rect x="10" y="8" width="3" height="12" rx="1" />
 <rect x="16" y="5" width="3" height="15" rx="1" />
 </svg>
 )}
 {b.icon ==="code" && (
 <svg
 viewBox="0 0 24 24"
 className="h-8 w-8"
 fill="none"
 stroke="white"
 strokeWidth="2"
 >
 <path
 d="M8 9l-4 3 4 3M16 9l4 3-4 3M13 7l-2 10"
 strokeLinecap="round"
 strokeLinejoin="round"
 />
 </svg>
 )}
 </div>
 <div className="font-semibold">{b.title}</div>
 </div>
 ))}
 </div>
 </div>
 </div>
 </div>
 </div>
 );
}

function ExplainerVideo() {
 return (
 <section id="explainer" className="bg-gray-50">
 <div className="mx-auto max-w-5xl px-4 py-16 text-center">
 <h2 className="text-3xl md:text-4xl font-extrabold text-heading mb-6">
 Watch How <span className="text-primary">SkillGenie</span> Works
 </h2>
 <p className="mb-8 text-muted">
 A quick walkthrough of our platform, features, and benefits.
 </p>

 {/* Video Embed */}
 <div className="aspect-video w-full overflow-hidden rounded-xl shadow-xl ring-1 ring-black/10">
 <video
 className="h-full w-full"
 src="/video.mp4"
 controls
 playsInline
 preload="metadata"
 />
 </div>
 </div>
 </section>
 );
}

/* ---------------- Limited-Time Offer band ---------------- */
function LimitedTimeOfferBar({ deadline, onRegister }) {
 const { d, h, m, s, over } = useCountdown(deadline);
 const go = () => {
 if (onRegister) return onRegister();
 const el = document.getElementById("final-cta");
 if (el) el.scrollIntoView({ behavior:"smooth" });
 };
 return (
 <section className="w-full bg-brand-midnight border-y border-secondary/30 py-8">
 <div className="max-w-7xl mx-auto px-6 text-center text-white">
 <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight">
 Limited-Time Offer
 </h3>
 <p className="text-sm sm:text-base text-white/90 mt-1">
 Next batch starts:{""}
 <span className="font-semibold text-primary">
 {deadline.toLocaleString("en-IN", {
 dateStyle:"medium",
 timeStyle:"short",
 })}
 </span>
 </p>

 <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
 {[
 { label:"Days", value: d },
 { label:"Hours", value: h },
 { label:"Minutes", value: m },
 { label:"Seconds", value: s },
 ].map((x) => (
 <div
 key={x.label}
 className="min-w-[90px] rounded-xl px-5 py-3 text-center
 bg-[#0b0b0b] border border-secondary/50"
 >
 <div className="text-2xl sm:text-3xl font-extrabold text-primary tabular-nums">
 {over ?"00" : String(x.value).padStart(2,"0")}
 </div>
 <div className="text-[10px] uppercase tracking-wider text-white/80">
 {x.label}
 </div>
 </div>
 ))}
 </div>

 <p className="max-w-3xl mx-auto mt-5 text-sm sm:text-base text-white/90">
 Early-bird registrations and fee waivers are available for a short
 window. Reserve your seat and boost your chances of learning with
 mentor-led projects.
 </p>
 <button
 onClick={go}
 className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary text-white font-bold px-6 py-3 shadow hover:scale-[1.02] active:scale-95 transition"
 >
 Register Now
 <svg
 viewBox="0 0 24 24"
 className="w-5 h-5"
 fill="none"
 stroke="currentColor"
 strokeWidth="2"
 >
 <path
 d="M5 12h14M13 5l7 7-7 7"
 strokeLinecap="round"
 strokeLinejoin="round"
 />
 </svg>
 </button>
 </div>
 </section>
 );
}

/* ---------------- Testimonials ---------------- */
function TestimonialsSection() {
 const Star = () => (
 <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#F59E0B">
 <path d="m12 17.27 5.18 3.05-1.64-5.81L20 10.5l-6-.26L12 4 10 10.24 4 10.5l4.46 4.01L6.82 20.3 12 17.27Z" />
 </svg>
 );

 const data = [
 {
 name:"Lalitha",
 role:"B.Tech 3rd Year - CSE",
 text:
"Skill Genie's Data Science bootcamp helped me finally understand machine learning in a practical way. The projects and built-in compiler made learning fun and hands-on.",
 avatar:
"https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRtIKv5GKQvxCdlf7mQ_o3cfcxGVo39KQMSiA&s",
 },
 {
 name:"Durga",
 role:"Final Year B.Tech - ECE",
 text:
"The Full-Stack Development course gave me the confidence to build real-world apps. I loved the way each module was structured and the Launchpad sessions were very helpful.",
 avatar:
"https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRtIKv5GKQvxCdlf7mQ_o3cfcxGVo39KQMSiA&s",
 },
 {
 name:"Satish",
 role:"Final Year B.Tech - IT",
 text:
"I was always afraid of coding interviews, but the challenge section on Skill Genie made me enjoy solving problems. It was a game-changer in preparing for placements.",
 avatar:
"https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRjHtHrbBuXgZzktciZOkg1ngfTawLK_0MkfA&s",
 },
 ];

 return (
 <section className="py-14">
 <div className="max-w-7xl mx-auto px-6">
 <div className="text-center mb-8">
 <div className="text-accent text-xs uppercase tracking-[0.2em]">
 Testimonials
 </div>
 <h2 className="text-2xl sm:text-3xl font-bold text-white mt-1">
 Our Clients Review
 </h2>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
 {data.map((t, i) => (
 <div
 key={i}
 className="relative rounded-2xl bg-white border border-secondary/30 p-6 pt-10 shadow-soft text-body"
 >
 <div className="absolute -top-6 left-1/2 -translate-x-1/2">
 <img
 src={t.avatar}
 alt={t.name}
 className="h-12 w-12 rounded-full ring-4 ring-bgSoft object-cover"
 />
 </div>

 <div className="absolute top-4 left-4 h-7 w-7 rounded-full bg-primary/10 text-primary flex items-center justify-center">
 <span className="text-lg leading-none">"</span>
 </div>

 <div className="mt-2 text-center">
 <h4 className="text-base sm:text-lg font-bold text-heading">{t.name}</h4>
 <div className="text-xs text-muted">{t.role}</div>

 <p className="mt-3 text-sm leading-relaxed text-body">
 {t.text}
 </p>

 <div className="mt-3 flex items-center justify-center gap-1">
 {Array.from({ length: 5 }).map((_, j) => (
 <Star key={j} />
 ))}
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 </section>
 );
}

/* ---------------- FAQ Section ---------------- */
function FAQSection() {
 const faqs = [
 {
 question:"What is SkillGenie?",
 answer:
"SkillGenie is a complete coding practice and career preparation platform. We combine bootcamps, coding challenges, role-based launchpads, technology docs, and certifications into one ecosystem. Unlike scattered online tutorials, SkillGenie provides a guided path from beginner to job-ready professional. You not only learn concepts but also apply them in real-world projects, get feedback from mentors, and validate your progress with verifiable certificates.",
 },
 {
 question:"Who can join?",
 answer:
"Our platform is designed for students, recent graduates, and working professionals looking to upskill or switch careers. Whether you are completely new to coding, preparing for interviews, or trying to build a portfolio, SkillGenie has structured tracks for each stage. We also provide advanced challenges for experienced developers who want to sharpen their problem-solving skills and stay competitive in tech interviews.",
 },
 {
 question:"Is it free?",
 answer:
"Yes! You can sign up for free and instantly access selected coding challenges, quizzes, and technology docs. This helps you explore our platform before committing to premium features. If you want to unlock advanced bootcamps, mentor feedback, team-based projects, and certification, you can upgrade to our premium plans.",
 },
 {
 question:"Do I get a certificate?",
 answer:
"Absolutely. SkillGenie provides QR-verifiable certificates and digital badges that you can showcase on LinkedIn, GitHub, or resumes. These validate your project completions, challenges solved, and technology stacks mastered. Recruiters and companies trust verifiable credentials, which makes your profile stand out.",
 },
 {
 question:"How are Bootcamps structured?",
 answer:
"Each bootcamp follows a 15-phase structure designed to simulate real workplace projects. You'll start with requirement gathering, move through design and implementation, and finish with testing and deployment. Mentors provide reviews and guidance. By the end, you'll have a professional-grade project to showcase to recruiters.",
 },
 ];

 const [openIndex, setOpenIndex] = useState(null);

 return (
 <section className="bg-bgMain py-16 border-t border-secondary">
 <div className="max-w-5xl mx-auto px-6">
 <h2 className="text-3xl font-extrabold text-center mb-10 text-primary">
 Frequently Asked Questions
 </h2>
 <div className="space-y-4">
 {faqs.map((faq, idx) => (
 <div
 key={idx}
 className={`border rounded-xl transition-all overflow-hidden ${
 openIndex === idx
 ?"bg-[#111827] border-primary"
 :"bg-[#1a1a1a] border-gray-700 hover:border-secondary"
 }`}
 >
 <button
 onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
 className="flex items-center justify-between w-full px-6 py-4 text-left font-semibold text-primary"
 >
 <span>{faq.question}</span>
 <svg
 className={`h-5 w-5 transform transition-transform ${
 openIndex === idx ?"rotate-180 text-primary" :""
 }`}
 fill="none"
 stroke="currentColor"
 strokeWidth="2"
 viewBox="0 0 24 24"
 >
 <path d="M19 9l-7 7-7-7" />
 </svg>
 </button>
 <div
 className={`px-6 pb-4 text-muted text-sm leading-relaxed transition-all duration-300 ease-in-out ${
 openIndex === idx
 ?"max-h-96 opacity-100"
 :"max-h-0 opacity-0 overflow-hidden"
 }`}
 >
 {faq.answer}
 </div>
 </div>
 ))}
 </div>
 </div>
 </section>
 );
}

/* ---------------- Scroll Trigger (open once @ 35%) ---------------- */
function useScrollOpenOnce(
 threshold = 0.35,
 storageKey ="sg_form_shown_35"
) {
 const [open, setOpen] = useState(false);

 useEffect(() => {
 if (typeof window ==="undefined") return;

 // Skip if already shown in this tab
 try {
 if (sessionStorage.getItem(storageKey) ==="1") return;
 } catch {}

 const getHeights = () => {
 const de = document.documentElement;
 const body = document.body;
 const scrollHeight = Math.max(de.scrollHeight, body?.scrollHeight || 0);
 const innerH = window.innerHeight || de.clientHeight || 1;
 return { scrollHeight, innerH };
 };

 const onScrollCheck = () => {
 const de = document.documentElement;
 const body = document.body;
 const { scrollHeight, innerH } = getHeights();
 const denom = Math.max(1, scrollHeight - innerH); // avoid /0

 // robust scrollTop across browsers
 const scrollTop =
 window.scrollY ??
 window.pageYOffset ??
 de.scrollTop ??
 body?.scrollTop ??
 0;

 const progress = scrollTop / denom; // 0..1

 if (progress >= threshold) {
 setTimeout(() => setOpen(true), 200);
 try { sessionStorage.setItem(storageKey,"1"); } catch {}
 window.removeEventListener("scroll", onScrollCheck);
 window.removeEventListener("resize", onScrollCheck);
 }
 };

 window.addEventListener("scroll", onScrollCheck, { passive: true });
 window.addEventListener("resize", onScrollCheck, { passive: true });

 // Run once on mount in case we already meet the threshold (e.g., short pages or after reload)
 onScrollCheck();

 return () => {
 window.removeEventListener("scroll", onScrollCheck);
 window.removeEventListener("resize", onScrollCheck);
 };
 }, [threshold, storageKey]);

 return [open, setOpen];
}


/* ---------------- Submission Form Modal (Portal) ---------------- */
function SubmissionFormModal({ open, onClose }) {
 const [form, setForm] = React.useState({
 name:"",
 email:"",
 phone:"",
 message:"",
 });
 const [sent, setSent] = React.useState(false);

 // Lock background scroll when open
 React.useEffect(() => {
 if (!open) return;
 const prev = document.body.style.overflow;
 document.body.style.overflow ="hidden";
 return () => {
 document.body.style.overflow = prev;
 };
 }, [open]);

 // Close on ESC
 React.useEffect(() => {
 const onKey = (e) => e.key ==="Escape" && onClose?.();
 if (open) window.addEventListener("keydown", onKey);
 return () => window.removeEventListener("keydown", onKey);
 }, [open, onClose]);

 const handleChange = (e) =>
 setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

 const handleSubmit = async (e) => {
 e.preventDefault();
 try {
 setSent(true); // show"Sending..."
 const res = await fetch(`${API_BASE_URL}/lead`, {
 method:"POST",
 headers: {"Content-Type":"application/json" },
 mode:"cors",
 body: JSON.stringify(form),
 });

 if (!res.ok) {
 // optional: read error details
 const err = await res.json().catch(() => ({}));
 throw new Error(err.error || `Request failed (${res.status})`);
 }

 // success - clear form, notify, close
 setForm({ name:"", email:"", phone:"", message:"" });
 setTimeout(() => {
 setSent(false);
 onClose?.();
 }, 700);
 } catch (err) {
 console.error("Lead submit error:", err);
 alert(err.message ||"Unable to submit right now.");
 setSent(false);
 }
};

 if (!open) return null;

 const modal = (
 <div
 className="fixed inset-0 z-[9999] flex items-center justify-center"
 role="dialog"
 aria-modal="true"
 >
 {/* Backdrop */}
 <div
 className="absolute inset-0 bg-bgMain/80 backdrop-blur-sm"
 onClick={onClose}
 />

 {/* Dialog */}
 <div className="relative w-[94%] max-w-4xl mx-auto">
 <div className="rounded-2xl bg-[#0b0b0b] border border-secondary shadow-[0_0_80px_rgba(253,224,71,0.4)] overflow-hidden">
 <button
 onClick={onClose}
 className="absolute right-3 top-3 h-9 w-9 rounded-full border border-secondary text-primary hover:bg-secondary/10"
 aria-label="Close"
 >
 ✕
 </button>

 <div className="grid grid-cols-1 md:grid-cols-[0.9fr_1.6fr]">
 {/* Left — Title + Logo */}
 <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-primary/60">
 <div className="rounded-xl border border-primary/50 p-6 h-full flex flex-col items-center justify-center text-center bg-[#0b0b0b]">
 <p className="text-primary font-semibold tracking-wider text-xs">
 SUBMIT YOUR
 </p>
 <h3 className="text-white text-xl font-extrabold mt-1">FORM</h3>

 {/* Logo */}
 <div className="mt-6 h-48 w-48 md:h-[13rem] md:w-[13rem] rounded-full bg-[#0e0e0e] border-2 border-secondary shadow-inner overflow-hidden flex items-center justify-center">
 <img
 src="/logoo.jpg"
 alt="Logo"
 className="h-full w-full object-contain scale-[1.35]"
 />
 </div>
 </div>
 </div>

 {/* Right — Form */}
 <div className="p-6 md:p-8">
 <form onSubmit={handleSubmit} className="space-y-4">
 <input
 name="name"
 placeholder="Name"
 value={form.name}
 onChange={handleChange}
 className="w-full rounded-lg bg-bgMain/60 text-white placeholder-gray-400 border border-primary/70 focus:border-primary outline-none px-4 py-3"
 required
 />
 <input
 name="email"
 type="email"
 placeholder="Email"
 value={form.email}
 onChange={handleChange}
 className="w-full rounded-lg bg-bgMain/60 text-white placeholder-gray-400 border border-primary/70 focus:border-primary outline-none px-4 py-3"
 required
 />
 <input
 name="phone"
 placeholder="Phone Number"
 value={form.phone}
 onChange={handleChange}
 className="w-full rounded-lg bg-bgMain/60 text-white placeholder-gray-400 border border-primary/70 focus:border-primary outline-none px-4 py-3"
 />
 <textarea
 name="message"
 placeholder="Message"
 rows={5}
 value={form.message}
 onChange={handleChange}
 className="w-full rounded-lg bg-bgMain/60 text-white placeholder-gray-400 border border-primary/70 focus:border-primary outline-none px-4 py-3"
 />
 <button
 type="submit"
 className="w-full rounded-full bg-primary text-white font-semibold py-3 hover:bg-primary active:scale-[0.99] transition shadow-[0_6px_24px_rgba(253,224,71,0.25)]"
 >
 {sent ?"Sending..." :"Send"}
 </button>
 {sent && (
 <div className="text-center text-green-400 text-sm">
 Submitted successfully!
 </div>
 )}
 </form>
 </div>
 </div>
 </div>
 </div>
 </div>
 );

 return createPortal(modal, document.body);
}

/* ===================== PAGE ===================== */
export default function LandingPage1() {
 // Stable countdown deadline
 const deadline = useMemo(
 () => new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
 []
 );

 // Form modal state + open-on-scroll
 const [formOpen, setFormOpen] = useScrollOpenOnce(0.5);

 const openForm = () => setFormOpen(true);

 return (
 <div className="min-h-screen bg-bgMain text-body font-sans overflow-x-hidden">
 {/* HERO (with floating carousel) */}
 <section
 className="relative"
 style={{
 backgroundImage:
"linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url('/bg2.jpg')",
 backgroundSize:"cover",
 backgroundPosition:"center",
 }}
 >
 <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 pt-16 pb-28">
 <div className="max-w-2xl">
 <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
 <span className="text-primary">Choose the right</span> theme
 for education
 </h1>
 <p className="text-gray-200 mt-4">
 Give wings to your degree with lessons created by experts. Master
 job-ready skills while you study. Built on a beautiful dark theme
 with SkillGenie's signature skyblue accent.
 </p>

 <div className="mt-6 flex gap-3">
 <button
 onClick={openForm}
 className="bg-primary text-white font-semibold px-5 py-3 rounded-lg shadow hover:scale-[1.02] active:scale-95 transition"
 >
 Get Started
 </button>
 <a
 href="#explainer"
 className="border border-secondary text-primary font-semibold px-5 py-3 rounded-lg hover:bg-secondary/10 transition"
 >
 Watch Demo
 </a>
 </div>
 </div>
 </div>

 <FloatingFeatureStrip />
 </section>

 {/* Spacer for overlapping strip */}
 <div className="h-32" />

 <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
 {/* Feature Row */}
 <section className="my-10">
 <h2 className="text-2xl md:text-3xl font-bold text-primary text-center mb-8">
 Why Choose Skill Genie
 </h2>
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
 {[
 {
 title:"Built-in Compiler",
 icon: CodeBracketIcon,
 color:"#EF4444",
 desc:"Practice and run your code instantly within our interactive coding environment.",
 },
 {
 title:"Launchpad",
 icon: RocketLaunchIcon,
 color:"#D946EF",
 desc:"Accelerate your tech career with real-world projects and placement support.",
 },
 {
 title:"Take Challenges",
 icon: BoltIcon,
 color:"#22C55E",
 desc:"Sharpen your skills through hands-on coding challenges and competitions.",
 },
 {
 title:"Bootcamp",
 icon: AcademicCapIcon,
 color:"var(--primary-blue, #4F46E5)",
 desc:"Join our intensive bootcamps led by industry experts to learn fast and effectively.",
 },
 ].map((c, idx) => (
 <div
 key={idx}
 className="rounded-xl overflow-hidden bg-white border border-gray-200 shadow-sm mb-8"
 >
 <div className="relative">
 <div
 className="h-28 flex items-center justify-center"
 style={{ backgroundColor: c.color }}
 >
 <div className="text-white drop-shadow-sm [&_svg]:!w-16 [&_svg]:!h-16">
 <c.icon />
 </div>
 </div>
 <div
 className="absolute left-1/2 -bottom-3 -translate-x-1/2 w-0 h-0
 border-l-[14px] border-r-[14px] border-t-[14px]"
 style={{
 borderLeftColor:"transparent",
 borderRightColor:"transparent",
 borderTopColor: c.color,
 }}
 />
 </div>
 <div className="p-5">
 <h3 className="font-semibold text-slate-900">{c.title}</h3>
 <p className="text-slate-500 text-sm leading-6 mt-2">
 {c.desc}
 </p>
 </div>
 </div>
 ))}
 </div>
 </section>

 {/* Courses */}
 <section className={`${panel} my-8`}>
 <h2 className="text-2xl md:text-3xl font-bold text-primary text-center mb-8">
 Our Courses
 </h2>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 {[
 {
 name:"Data Scientist",
 dur:"45 days",
 pts: ["Python & Pandas","Data Visualization","Model Evaluation"],
 },
 {
 name:"Artificial Intelligence Engineer",
 dur:"45 days",
 pts: ["Deep Learning","NLP Basics","TensorFlow/Keras"],
 },
 {
 name:"Mobile Application Developer",
 dur:"45 days",
 pts: ["Flutter/Dart","UI/UX Design","Firebase Integration"],
 },
 {
 name:"Software Developer",
 dur:"45 days",
 pts: ["OOP Concepts","Version Control","System Design Basics"],
 },
 {
 name:"Java Full Stack Developer",
 dur:"45 days",
 pts: ["Java + Spring Boot","MySQL/PostgreSQL","React.js"],
 },
 {
 name:"Robotics and Automation",
 dur:"45 days",
 pts: ["Embedded Systems","Arduino + Sensors","Automation Logic"],
 },
 {
 name:"Web Developer",
 dur:"45 days",
 pts: ["HTML/CSS/JS","React & Node.js","Responsive Design"],
 },
 {
 name:"Python Developer",
 dur:"45 days",
 pts: ["Python Core","Flask/Django","REST APIs"],
 },
 {
 name:"Java Developer",
 dur:"45 days",
 pts: ["Core Java","Spring Framework","JPA/Hibernate"],
 },
 {
 name:"Machine Learning Engineer",
 dur:"45 days",
 pts: [
"Supervised & Unsupervised Learning",
"Scikit-Learn",
"Model Deployment",
 ],
 },
 ].map((c, idx) => (
 <div key={idx} className={`${card} flex flex-col`}>
 <div className="flex items-center justify-between mb-2">
 <h3 className="text-primary font-semibold">{c.name}</h3>
 <span className="text-xs bg-bgMain/50 border border-secondary text-primary px-2 py-1 rounded">
 {c.dur}
 </span>
 </div>
 <ul className="text-muted text-sm space-y-1 mb-3 list-disc ml-4">
 {c.pts.map((p) => (
 <li key={p}>{p}</li>
 ))}
 </ul>
 <div className="mt-auto pt-2 border-t border-gray-700">
 <span className="text-xs text-primary">
 ✅ Certificate upon successful completion
 </span>
 </div>
 </div>
 ))}
 </div>
 </section>

 {/* Stats */}
 <section className="my-12">
 <div
 className="rounded-2xl p-6 sm:p-8 text-white overflow-hidden"
 style={{
 background:
 "radial-gradient(900px 300px at 0% 0%, #1E3A8A 0%, #0B1023 100%)",
 }}
 >
 <div className="grid md:grid-cols-4 gap-6 items-stretch">
 <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-6">
 {[
 { value:"1,500+", label:"Learners trained" },
 { value:"89", label:"Expert mentors & partners" },
 { value:"12", label:"Average sessions / month" },
 ].map((s) => (
 <div
 key={s.label}
 className="rounded-xl p-6 text-center shadow-[0_6px_24px_rgba(0,0,0,0.25)]
 border border-accentGray/50 bg-white/10"
 style={{ backdropFilter:"blur(2px)" }}
 >
 <div className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">
 {s.value}
 </div>
 <div className="mt-2 text-sm text-violet-200">
 {s.label}
 </div>
 </div>
 ))}
 </div>

 <div className="md:col-span-1 flex items-center">
 <div>
 <h3 className="text-lg sm:text-xl font-semibold leading-snug">
 We've unlocked the potential of more than{""}
 <span className="font-extrabold text-violet-300">1,500</span>{""}
 devs, designers and analysts.
 </h3>
 <p className="mt-3 text-sm text-violet-200/90">
 Join a cohort thatâ€™s serious about outcomes-projects,
 referrals and placement support.
 </p>
 </div>
 </div>
 </div>
 </div>
 </section>

 <ExplainerVideo />

 {/* Testimonials */}
 <TestimonialsSection />

 {/* Countdown band (stable deadline) */}
 <LimitedTimeOfferBar deadline={deadline} />

 {/* FAQ Section */}
 <FAQSection />

 {/* Final CTA */}
 <section id="final-cta" className="py-12">
 <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
 <div className={`${panel} flex flex-col md:flex-row items-center gap-6`}>
 <div className="flex-1">
 <h3 className="text-2xl md:text-3xl font-extrabold text-primary">
 Ready to start learning?
 </h3>
 <p className="text-muted mt-1">
 Pick a track and join the next cohort. Learn by building.
 </p>
 </div>
 <button
 onClick={() => setFormOpen(true)}
 className="bg-primary text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:scale-[1.02] active:scale-95 transition"
 >
 Apply Now
 </button>
 </div>
 </div>
 </section>
 </main>

 {/* Form Modal */}
 <SubmissionFormModal open={formOpen} onClose={() => setFormOpen(false)} />
 </div>
 );
}




